import { Component, OnInit } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { FormRepository } from '../../Repositories';
import { BsModalRef } from 'ngx-bootstrap/modal';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    CsvExportParams,
    ApplyColumnStateParams
} from 'ag-grid-community';
import { AgGridOptionsBuilder } from '../../AgGrid';
import { AgGridFilterParams, AgGridColumns } from '../../AgGrid';
import { IWeissmanModalComponent } from '../../WeissmanModalService';
import { FormService } from '../form.service';
import { AgGridLinkCellRendererComponent, AgGridLinkCellRendererParams } from '../../AgGrid/CellRenderers/agGridLinkCellRenderer.component';

@Component({
    selector: 'form-filing-batch-list',
    templateUrl: './formFilingBatchList.component.html',
    styleUrls: ['./formFilingBatchList.component.scss'],
    standalone: false
})
export class FormFilingBatchListComponent implements OnInit, IWeissmanModalComponent<void, void> {
    constructor(
        private readonly _formRepository: FormRepository,
        private readonly _formService: FormService,
        private readonly _bsModalRef: BsModalRef) {
    }

    params: void;
    result: void;

    isBusy: boolean = false;
    gridApi: GridApi;
    gridOptions: GridOptions = new AgGridOptionsBuilder()
        .withContext(this)
        .withLoadingOverlay()
        .withFloatingFilter()
        .withMultipleColumnSort()
        .withColumnResize()
        .withTextSelection()
        .build();

    private _filingBatches: Compliance.FilingBatchInfoModel[];

    get headerLabel(): string {
        return `In-Use Filing Batches for ${this._formService.assessorName}`;
    }

    async ngOnInit(): Promise<void> {
        this.isBusy = true;

        try {
            this._filingBatches = await lastValueFrom(this._formRepository.getFilingBatches(this._formService.assessorId));
            this._setRowData();
        } finally {
            this.isBusy = false;
        }
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;

        const columns: ColDef[] = [
            {
                headerName: 'Batch #',
                field: 'displayId',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    getHelpContentId: (params: AgGridLinkCellRendererParams) => 'app.view-filing-batch',
                    newWindow: true,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        const filingBatch = params.data as Compliance.FormRevisionFilingBatchInfoModel;
                        if (!(filingBatch && filingBatch.filingBatchId)) {
                            return '';
                        }
                        return `#/returnBatch/${filingBatch.filingBatchId}`;
                    }
                } as AgGridLinkCellRendererParams
            },
            {
                headerName: 'Progress Status',
                field: 'statusCode',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'Assessor',
                field: 'assessorName',
                lockVisible: true,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    getHelpContentId: (params: AgGridLinkCellRendererParams) => 'app.view-assessor',
                    newWindow: true,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        const filingBatch = params.data as Compliance.FormRevisionFilingBatchInfoModel;
                        if (!(filingBatch && filingBatch.assessorId)) {
                            return '';
                        }
                        return `#/assessor/${filingBatch.assessorId}`;
                    },
                    isDisabled: (params: AgGridLinkCellRendererParams) => {
                        const filingBatch = params.data as Compliance.FormRevisionFilingBatchInfoModel;
                        return !(filingBatch && filingBatch.assessorId);
                    }
                } as AgGridLinkCellRendererParams,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            }
        ];

        const defaultSortModel: ApplyColumnStateParams = {
            state: [{ colId: 'displayId', sort: 'asc' }],
            defaultState: { sort: null }
        };

        this.gridApi.setGridOption('columnDefs', columns);
        this.gridApi.applyColumnState(defaultSortModel);
        this._setRowData();
    }

    cancel(): void {
        this._bsModalRef.hide();
    }

    export(): void {
        const params = {
            fileName: 'FormFilingBatches.csv'
        } as CsvExportParams;
        this.gridApi.exportDataAsCsv(params);
    }

    private _setRowData() {
        if (!(this.gridApi && this._filingBatches)) {
            return;
        }

        this.gridApi.setGridOption('rowData', this._filingBatches);
        this.gridApi.sizeColumnsToFit();
    }
}
